import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { PermissionType, ProjectPermissionType, Role } from '@/stores/type/authorization.type'
import { useApi } from '@/api/useApi'
import API from '@/api/apiUrls'

import { useBaseStore } from '@haskoning/vue-basestore'

const api = useApi<Role>()
const baseUrl = API.ROLES.INDEX

export const useAuthorizationStore = defineStore('authorization', () => {
	const baseStore = useBaseStore<Role>({ service: baseUrl, api })

	const currentRoles = ref<Role[]>([])

	// Actions
	const setCurrentRole = (roleIds: number[]) => {
		roleIds.forEach((roleId: number) => {
			const role = getCurrentRole(roleId)
			const isExist = currentRoles.value.find((item: Role) => item.id === roleId)
			if (role && !isExist) {
				currentRoles.value.push(role)
			}
		})
	}

	const getCurrentRole = (roleId: number) => {
		return baseStore.items.value.find((item: Role) => item.id === roleId)
	}

	const hasServicePermission = ({
		authSource,
		allowed
	}: {
		authSource: string
		allowed: string[]
	}) => {
		const authRoles = currentRoles.value.find((role: Role) => role.auth_source === authSource)
		return authRoles
			? authRoles.permissions.some((permission: string) => allowed.includes(permission))
			: false
	}

	const resetState = () => {
		baseStore.items.value = []
		currentRoles.value = []
		setCurrentRole([])
	}

	const getServicePermissionByRole = async ({
		roleItems,
		permission
	}: {
		roleItems: { id: number; role: number }[]
		permission: string[]
	}): Promise<ProjectPermissionType[]> => {
		resetState()
		await baseStore.fetchItems()
		const authPermission: ProjectPermissionType[] = []
		roleItems.forEach((roleItem) => {
			const authPermissionItem: PermissionType[] = []
			permission.forEach((permissionItem) => {
				const authRoles = getCurrentRole(roleItem.role)
				if (!authRoles) return
				const hasPermission = authRoles.permissions.includes(permissionItem)
				if (hasPermission) {
					const p = permissionItem.split('_')[0] as PermissionType
					authPermissionItem.push(p)
				}
			})
			authPermission.push({
				id: roleItem.id,
				role: roleItem.role,
				permissions: authPermissionItem
			})
		})
		return authPermission
	}

	return {
		...baseStore,
		currentRoles,
		setCurrentRole,
		getCurrentRole,
		hasServicePermission,
		resetState,
		getServicePermissionByRole
	}
})
